import { useState, useRef, useEffect } from "react";
import { setCitationData } from "context/features/dataSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { generateCitation } from "features/Bookmarks/services/citation.service";
import { useSelector } from "react-redux";
import { logEvent } from "services/amplitude.service";
import { getEbscoRecordId } from "utils/database-source.util";

const useCitationGenerate = (
  requiredFields,
  articleData,
  setLoading,
  citation,
  setCitation,
  currentItemType,
  selectedStyle,
  authors,
  setDisable,
  selectedBookmarkOption
) => {
  const dispatch = useDispatch();
  const [tooltipText, setTooltipText] = useState("Copy");
  const [citationGenerating, setCitationGenerating] = useState(false);
  const abortControllerRef = useRef(null);
  const latestRequestRef = useRef(0);

  const { isCitationAvailable, summaryInfoData } = useSelector(
    (state) => state.data
  );

  const handleMouseLeave = () => {
    setTooltipText("Copy");
  };

  const handleCopyClick = () => {
    const textToCopy = citation;
    const html = new Blob([textToCopy], { type: "text/html" });
    const plain = new Blob([textToCopy.replace(/<[^>]*>?/gm, "")], {
      type: "text/plain",
    });

    navigator.clipboard
      .write([
        new ClipboardItem({
          "text/html": html,
          "text/plain": plain,
        }),
      ])
      .then(() => {
        setTooltipText("Copied!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const fetchCitation = async (fields, isDataModified, userTriggered = false) => {
    const ebscoRecord = getEbscoRecordId(summaryInfoData);
    const hasEbscoCitation = Boolean(ebscoRecord);
    if (isCitationAvailable && !isDataModified) return;
    if (!hasEbscoCitation && Object.keys(fields).length === 0) return;
    const anyFieldMissing = requiredFields.some((field) => {
      return !articleData[field];
    });
    if (!hasEbscoCitation && anyFieldMissing) {
      console.error("Please fill in all required fields.");
      setLoading(false);
      setDisable(true);
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    // if (!citation) setLoading(true);

    // Database citations have nothing else on screen, so show a loader while
    // they generate. Only the latest request may update the result.
    const requestId = ++latestRequestRef.current;
    if (hasEbscoCitation) setCitationGenerating(true);

    try {
      const data = await generateCitation(
        currentItemType,
        selectedStyle,
        articleData,
        authors,
        ebscoRecord || {}
      );
      if (requestId !== latestRequestRef.current) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(data, "text/html");
      const entryElement = doc.querySelector(".csl-entry");
      // Database citations may come back without the CSL wrapper.
      const citationHTML = entryElement
        ? entryElement.innerHTML
        : hasEbscoCitation
          ? doc.body.innerHTML.trim()
          : "";

      if (citationHTML) {
        setCitation(citationHTML);
        dispatch(setCitationData(citationHTML));
      } else if (hasEbscoCitation) {
        // Don't leave the previous style's citation showing.
        setCitation("");
        dispatch(setCitationData(""));
        toast.error("The database couldn't generate a citation in this style.");
      }

      if (userTriggered && selectedBookmarkOption === "citation")
        logEvent("create_citation", {
          click: true,
          url: summaryInfoData.url,
        });
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error(
          "Some error occurred while generating citation. Please try again."
        );
      }
    } finally {
      setLoading(false);
      if (requestId === latestRequestRef.current) setCitationGenerating(false);
    }
  };

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    fetchCitation,
    citationGenerating,
    tooltipText,
    handleCopyClick,
    handleMouseLeave,
  };
};

export default useCitationGenerate;
